"use client";

import React, { useState, useEffect, useRef } from "react";
import { ArrowRight, Shield, MessageSquare, Network, Hammer, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
const steps = [{
  id: "01.",
  title: "尋求諮詢",
  englishTitle: "CONSULTATION",
  description: "親臨展間，我們將協助您挑選最適合居家風格與使用習慣的地板材質與色調。",
  icon: MessageSquare
}, {
  id: "02.",
  title: "現場丈量",
  englishTitle: "MEASUREMENT",
  description: "專業工班實地勘查，精確測量施作坪數並評估地面平整度，確保後續施工品質達到最高標準。",
  icon: Network
}, {
  id: "03.",
  title: "專業施工",
  englishTitle: "INSTALLATION",
  description: "由經驗豐富的師傅進行精準鋪設，注重每一處接縫與收邊細節，為您打造完美的足下工藝空間。",
  icon: Hammer
}, {
  id: "04.",
  title: "售後保固",
  englishTitle: "WARRANTY",
  description: "完工後提供地板清潔、保養及使用注意事項，相關保固內容依產品與施工保固規範辦理，讓您安心使用，長久享受舒適生活。",
  icon: ShieldCheck
}];
export default function ServiceImportProcess() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!containerRef.current) return;
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const rect = containerRef.current!.getBoundingClientRect();
          const windowHeight = window.innerHeight;
          const scrollTriggerStart = windowHeight * 0.85;
          const totalScrollableHeight = rect.height;
          const currentScrollPos = scrollTriggerStart - rect.top;
          let newProgress = currentScrollPos / totalScrollableHeight * 1.15;
          newProgress = Math.max(0, Math.min(1, newProgress));
          setProgress(prev => Math.abs(prev - newProgress) > 0.001 ? newProgress : prev);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="服務流程-service-process" className="w-full bg-[#f3ede5] py-24 relative overflow-hidden select-none">
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        
     {/* ==========================================
      1. 頂部中英文雙層置中標題 (字級同步放大版)
      ========================================== */}
<div className="mb-24 text-center flex flex-col items-center max-w-5xl mx-auto">
  
  {/* 小副標組 */}
  <div className="flex items-center justify-center gap-3 mb-4 text-center">
    {/*  放大到 text-sm (14px) */}
    <span className="font-hk-light text-sm tracking-widest text-[#3d3d3d]/80">
      接下來換你了
    </span>
    {/*  斜線同步放大到 text-sm */}
    <span className="font-sans text-sm font-light text-[#d3bfae]">/</span>
    {/*  英文副標同步放大到 text-xs (12px) */}
    <span className="font-sans text-xs font-semibold tracking-[0.2em] text-[#d3bfae] uppercase">
      SERVICES PROCESS
    </span>
  </div>

  {/* 帶有燕麥色高光底襯的主標題 */}
  <div className="relative inline-block px-2">
    <div className="absolute bottom-0 left-0 w-full h-[35%] bg-[#d3bfae]/30 z-0" />
    {/*  主標題同步推大到 text-3xl md:text-4xl，與下方的時間軸大字體更有承接感 */}
    <h2 className="relative z-10 font-hk-regular text-3xl md:text-4xl font-normal tracking-widest text-[#3d3d3d]">
      從需求到施工 | 我們陪你一起
    </h2>
  </div>
</div>

        {/* ==========================================
            2. 時間軸本體：左右交錯佈局
           ========================================== */}
        <div ref={containerRef} className="relative max-w-5xl mx-auto">
          
          {/* 工藝級垂直主軸線 */}
          <div className="absolute left-[19px] md:left-1/2 md:-translate-x-1/2 top-6 bottom-10 w-[1px] z-0">
            <div className="absolute inset-0 w-full h-full bg-[#a98e70]/20" />
            <div className="absolute top-0 left-0 w-full bg-[#a98e70] transition-all duration-500 ease-out" style={{
            height: `${progress * 100}%`
          }} />
          </div>

          {/* 步驟列表 */}
          <div className="space-y-24 md:space-y-40">
            {steps.map((step, index) => {
            const isEven = index % 2 === 0;
            const threshold = index / (steps.length - 1) * 0.85;
            const isActive = progress >= threshold;
            return <div key={step.id} className={cn("relative flex flex-col md:flex-row items-start md:items-center", {
              "md:flex-row-reverse": isEven
            })}>
                  
                  {/* 文字內容區 (占 45% 寬度) */}
                  <div className="w-full md:w-[45%] pl-16 md:pl-0">
                    <div className={cn("transition-all duration-1000 transform flex flex-col gap-1 text-left items-start", {
                  "md:items-end": !isEven
                }, isActive ? "opacity-100 translate-y-0" : "opacity-30 translate-y-4")}>
                      
                      {/* 標題列組 */}
                      <div className="flex items-baseline gap-4 md:gap-5 flex-wrap">
                        {/*  大數字：再放大為 text-3xl md:text-4xl */}
                        <span className="font-sans text-3xl md:text-4xl font-semibold tracking-wider text-[#a98e70]">
                          {step.id}
                        </span>
                        
                        {/* 中英文垂直咬合容器 */}
                        <div className="flex flex-col items-start text-left">
                          {/*  中文標題：再放大為 text-xl md:text-2xl */}
                          <h3 className="font-hk-regular text-xl md:text-2xl font-normal tracking-widest text-[#a98e70] leading-none">
                            {step.title}
                          </h3>
                          {/*  英文標題：再放大為 text-xs md:text-sm */}
                          <span className="font-sans text-xs md:text-sm font-light tracking-[0.15em] text-[#a98e70] uppercase mt-2">
                            {step.englishTitle}
                          </span>
                        </div>
                      </div>

                      {/* 描述文字區與垂直短線
                          -  md:max-w-[460px]：配合字體放大，將安全防禦寬度加寬，排版更舒暢大氣
                       */}
                      <div className={cn("mt-5 pl-4 border-l-2 border-[#a98e70] text-left w-full max-w-xl md:max-w-[460px]", {
                    "md:max-w-[480px]": isEven
                  })}>
                        {/*  說明文字：再放大為 text-[15px] md:text-base */}
                        <p className="font-hk-light text-[15px] md:text-base leading-relaxed tracking-widest text-[#a98e70]">
                          {step.description}
                        </p>
                      </div>

                    </div>
                  </div>

                  {/* 時間軸 Icon 徽章圈圈 */}
                  <div className="absolute left-0 md:left-1/2 md:-translate-x-1/2 top-1.5 md:top-auto flex items-center justify-center z-20">
                    <div className={cn("size-10 md:size-11 rounded-full border flex items-center justify-center transition-all duration-500 shadow-md", isActive ? "bg-[#a98e70] border-[#a98e70] scale-110 shadow-[#a98e70]/10" : "bg-[#f3ede5] border-[#a98e70]/30 scale-100")}>
                      <step.icon className={cn("size-4.5 md:size-5 transition-colors duration-500", isActive ? "text-white" : "text-[#a98e70]/60")} strokeWidth={1.5} />
                    </div>
                  </div>

                  {/* 電腦版佔位格 */}
                  <div className="hidden md:block md:w-[45%]" />
                </div>;
          })}
          </div>

     

        </div>

        {/* ==========================================
            3. 底部人文寬版互動按鈕
           ========================================== */}
        <div className="mt-28 text-center">
          <Link to="/ContactUsPage">
            <Button size="lg" className="h-14 rounded-none px-10 text-xs tracking-widest transition-all bg-[#604f45] hover:bg-[#4a3d35] text-white border-none shadow-sm cursor-pointer">
              <span className="flex items-center gap-2">
                展開您的專屬工程規劃
                <ArrowRight className="size-3.5" />
              </span>
            </Button>
          </Link>
        </div>

      </div>
    </section>;
}